
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ISorter, IFilter } from "@/models/tables";
import { DefaultFilters } from "@/data/filters/importFiles";
import { DefaultSorters } from "@/data/sorters/importFiles";

// Helpers
import { downloadBlob } from "@/helpers/file";

// Types
import {
  IImportFilesProviderData,
  IImportFilesProviderMethods
} from "@/models/providers/importFiles";
import { IFile } from "@/models/files";

@Component
export default class ImportFilesProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: `IFP` /** [I]mport [F]iles [P]rovider */ })
  public declare id: string;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultFilters() })
  public availableFilters!: IFilter[];

  @Prop({ default: () => DefaultSorters() })
  public availableSorters!: ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  @Prop({ default: null })
  public usedDataModule!: DataModules;

  // Data

  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;
  public hasRequestError = false;

  public dataModule = this.usedDataModule || DataModules.IMPORTS_FILES;
  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);
  public searchQuery = "";

  public filters = this.availableFilters;
  public sorters = this.availableSorters;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.getData();
  }

  // Methods

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          brand_id: 0,
          ...this.queryFilter
        }
      },
      params // Important – must merge last
    );
  }

  public async doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();

    this.searchQuery = value;

    return this.getData({
      ignoreStored: true,
      page: 1
    });
  }

  public async openUploadFilesModal() {
    return this.$store.dispatch(
      `data/${DataModules.IMPORTS_UPLOAD_FILES}/openUploadFilesModal`,
      { events: { success: this.reloadData } }
    );
  }

  public async download(file: IFile) {
    try {
      if (!this.canDownload) return;

      const data = await this.$store.dispatch(
        `data/${DataModules.IMPORTS_UPLOAD_FILES}/download`,
        {
          fileId: file.id,
          vm: this
        }
      );

      const blob = new Blob([data], {
        type: "text/csv;charset=utf-8;"
      });

      const filename = `${file.original_name}.csv`;
      downloadBlob(blob, filename);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async delete(file: IFile) {
    if (!this.canDelete) return;

    this.$store.dispatch(
      `data/${DataModules.IMPORTS_UPLOAD_FILES}/confirmDelete`,
      {
        message: this.$t("_sentence.imports.delete_file", {
          fileName: `${file.original_name} (${file.name})`
        }),
        fileId: file.id,
        onSuccess: this.reloadData
      }
    );
  }

  // Computed

  public get queryFilter() {
    return this.searchQuery
      ? { "filter[original_name|like]": `%${this.searchQuery}%` }
      : {};
  }

  public get canUpload() {
    return this.$userCan("create_files");
  }

  public get canDownload() {
    return this.$userCan("download_files");
  }

  public get canDelete() {
    return this.$userCan("delete_files");
  }

  // Guarding

  public guardFunctionalities = ["list_detached_files"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("importFilesProviderData")
  public providerData(): IImportFilesProviderData {
    return {
      availableFilters: this.filters,
      availableSorters: this.sorters,
      activeFilters: this.activeFilters,
      canDelete: this.canDelete,
      canDownload: this.canDownload,
      canUpload: this.canUpload,
      constQueryFilters: this.constQueryFilters,
      activeSort: this.table.sort,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      files: this.table.data as IFile[],
      hasRequestError: this.hasRequestError,
      isLoading: this.table.loading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      searchQuery: this.searchQuery,
      table: this.table,
      total: this.table.total,
      guardFunctionalities: this.guardFunctionalities
    };
  }

  @Provide("importFilesProviderMethods")
  public get providerMethods(): IImportFilesProviderMethods {
    return {
      debounceSearch: this.debounceSearch,
      delete: this.delete,
      doQuickSearch: this.doQuickSearch,
      download: this.download,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      openUploadFilesModal: this.openUploadFilesModal
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $importFilesData: this.providerData(),
        $importFilesMethods: this.providerMethods
      })
    );
  }
}
